import { useContext, useState, useRef, useEffect } from 'react';
import { ThemeContext } from '@/theme/ThemeProvider';
import { Mission, StandardTier } from '@/app/hooks/trade/tradeMission';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'react-i18next';
import RewardButton from '../../RewardButton';
import { getClaimStatus } from '../../utils';
import * as S from './TierDetailModalStyles';
import { IoClose, IoChevronBack, IoChevronForward } from 'react-icons/io5';

interface TierDetailModalProps {
  mission: Mission;
  isOpen: boolean;
  onClose: () => void;
  onClaimReward: (
    missionId: number,
    tierIndex?: number,
    rewardAmount?: string | number
  ) => Promise<void>;
}

// TODO: 현재는 레버리지 미션만 있어서, 거기에 맞춰져 있습니다.추후 티어가 존재하는 기능미션이 추가되면 수정이 필요합니다.
export const TierDetailModal = ({
  mission,
  isOpen,
  onClose,
  onClaimReward,
}: TierDetailModalProps) => {
  const { isDarkMode } = useContext(ThemeContext);
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  // 슬라이더 상태 (현재 카드 인덱스)
  const [currentIndex, setCurrentIndex] = useState(0);

  // 모달이 열릴 때 인덱스 초기화
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const tiers = (mission?.conditions?.tiers as StandardTier[]) || [];
  const CARDS_PER_VIEW = 2; // 한 번에 보이는 카드 수

  // 왼쪽/오른쪽 버튼 표시 여부
  const showLeftArrow = currentIndex > 0;
  const showRightArrow = currentIndex < tiers.length - CARDS_PER_VIEW;

  // 왼쪽으로 이동
  const handlePrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);

      if (scrollRef.current) {
        const cardWidth = scrollRef.current.scrollWidth / tiers.length;
        scrollRef.current.scrollTo({
          left: cardWidth * newIndex,
          behavior: 'smooth',
        });
      }
    }
  };

  // 오른쪽으로 이동
  const handleNext = () => {
    if (currentIndex < tiers.length - CARDS_PER_VIEW) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);

      if (scrollRef.current) {
        const cardWidth = scrollRef.current.scrollWidth / tiers.length;
        scrollRef.current.scrollTo({
          left: cardWidth * newIndex,
          behavior: 'smooth',
        });
      }
    }
  };

  return (
    <>
      <S.Overlay onClick={onClose} isOpen={isOpen} />
      <S.ModalContainer isDarkmode={isDarkMode} isOpen={isOpen}>
        {/* Header */}
        <S.Header isDarkmode={isDarkMode}>
          <S.HeaderTitle>{mission.name}</S.HeaderTitle>
          <S.CloseButton onClick={onClose}>
            <IoClose size={24} color={isDarkMode ? '#FFFFFF' : '#000000'} />
          </S.CloseButton>
        </S.Header>

        {/* Tier 카드 슬라이딩 영역 */}
        <S.TierCarouselSection>
          {/* 왼쪽 화살표 */}
          {showLeftArrow && (
            <S.ArrowButton
              direction="left"
              onClick={handlePrevious}
              isDarkmode={isDarkMode}
            >
              <IoChevronBack size={24} />
            </S.ArrowButton>
          )}

          {/* 오른쪽 화살표 */}
          {showRightArrow && (
            <S.ArrowButton
              direction="right"
              onClick={handleNext}
              isDarkmode={isDarkMode}
            >
              <IoChevronForward size={24} />
            </S.ArrowButton>
          )}

          <S.TierCardWrapper ref={scrollRef}>
            {tiers.map((tier, index) => {
              const claimStatus = getClaimStatus(mission, index);
              const isAchieved =
                mission.userProgress?.achievedTiers?.includes(index);
              const isClaimed =
                mission.userProgress?.claimedTiers?.includes(index);

              return (
                <S.TierCard
                  key={index}
                  isDarkmode={isDarkMode}
                  isAchieved={!!isAchieved}
                >
                  {/* Tier 번호 배지 */}
                  <S.TierBadge isAchieved={!!isAchieved}>
                    {t('tier')} {index + 1}
                  </S.TierBadge>

                  {/* 목표 조건 */}
                  <S.GoalSection>
                    <S.SectionTitle isDarkmode={isDarkMode}>
                      {t('achieveCondition')}
                    </S.SectionTitle>
                    {tier.minVolume && (
                      <S.GoalItem>
                        <S.GoalLabel>{t('volume')}</S.GoalLabel>
                        <S.GoalValue>
                          {new BigNumber(tier.minVolume).toFormat()} USDT
                        </S.GoalValue>
                      </S.GoalItem>
                    )}
                    {tier.minLeverage && (
                      <S.GoalItem>
                        <S.GoalLabel>{t('leverage')}</S.GoalLabel>
                        <S.GoalValue>{tier.minLeverage}x</S.GoalValue>
                      </S.GoalItem>
                    )}
                  </S.GoalSection>

                  {/* 보상 금액 */}
                  <S.RewardSection isDarkmode={isDarkMode}>
                    <S.RewardLabel>{t('Reward')}</S.RewardLabel>
                    <S.RewardAmount>
                      {new BigNumber(tier.rewardAmount).toFormat()} USDT
                    </S.RewardAmount>
                  </S.RewardSection>

                  {/* 보상 수령 버튼 */}
                  <S.ButtonSection>
                    {isClaimed ? (
                      <S.ClaimedBadge isDarkmode={isDarkMode}>
                        ✓ {t('Rewarded')}
                      </S.ClaimedBadge>
                    ) : isAchieved ? (
                      <RewardButton
                        missionId={mission.id}
                        canClaimable={claimStatus === 'canClaim'}
                        getMissionStatus={claimStatus}
                        handleClaim={() =>
                          onClaimReward(mission.id, index, tier.rewardAmount)
                        }
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 700,
                          borderRadius: '8px',
                        }}
                      />
                    ) : (
                      <S.NotAchievedBadge isDarkmode={isDarkMode}>
                        {t('notAchieved')}
                      </S.NotAchievedBadge>
                    )}
                  </S.ButtonSection>
                </S.TierCard>
              );
            })}
          </S.TierCardWrapper>
        </S.TierCarouselSection>
      </S.ModalContainer>
    </>
  );
};
